<div class="offcanvas-header border-bottom">
    <h5 class="offcanvas-title" id="history-drawer-title"><i class="bi bi-clock-history me-2" aria-hidden="true"></i>{{ title() }}</h5>
    <button type="button" class="btn btn-sm btn-outline-primary ms-auto me-3 text-nowrap"
            [disabled]="loading() || events().length === 0"
            [title]="'Exports the ' + events().length + ' loaded entries. Load more first to include older ones.'"
            (click)="exportCsv()">
        <i class="bi bi-download me-1" aria-hidden="true"></i> Export CSV
    </button>
    <button type="button" class="btn-close ms-0" aria-label="Close" (click)="closed.emit()"></button>
</div>

<div class="offcanvas-body">
    <ng-content />

    @if (layout() === 'timeline') {
        @for (day of days(); track day.key) {
            <div class="fw-semibold text-body-secondary mt-4 mb-2">{{ day.heading }}</div>

            @for (event of day.items; track eventKey(event)) {
                <div class="callout my-2" [class]="kindCalloutClass(event.kind)">
                    <div class="d-flex justify-content-between flex-wrap gap-2">
                        <span class="fw-semibold">
                            <i class="bi me-1" [class]="tableDisplay(event.tableName).icon"></i>
                            {{ tableDisplay(event.tableName).label }}
                            <span class="fw-normal">
                                <i class="bi bi-chevron-compact-right"></i>
                                <span class="font-monospace">{{ event.record }}</span>
                                @if (event.customerName) {
                                    <i class="bi bi-chevron-compact-right"></i>
                                    <span>{{ event.customerName }}</span>
                                }
                            </span>
                        </span>
                        <span class="badge" [class]="kindBadgeClass(event.kind)">{{ event.kind.toUpperCase() }}</span>
                    </div>

                    <div class="mt-1">{{ event.summary }}</div>

                    <div class="small text-body-secondary d-flex flex-wrap column-gap-4 mt-1">
                        <span>{{ formatTime(event.occurredAt) }}</span>
                        <span>by {{ event.userName }}</span>
                    </div>

                    <details class="mt-2 small">
                        <summary class="text-body-secondary">Raw log rows ({{ event.rows.length }})</summary>
                        <div class="table-responsive">
                            <table class="table table-sm table-striped mb-0 mt-1">
                                <thead>
                                <tr>
                                    <th scope="col">Column</th>
                                    <th scope="col">Old value</th>
                                    <th scope="col">New value</th>
                                    <th scope="col">Logged</th>
                                </tr>
                                </thead>
                                <tbody>
                                    @for (row of event.rows; track row.id) {
                                        <tr>
                                            <td class="font-monospace">{{ row.columnName }}</td>
                                            <td class="raw-value">{{ row.oldValue ?? '' }}</td>
                                            <td class="raw-value">{{ row.newValue ?? '' }}</td>
                                            <td class="text-nowrap">{{ formatDateTime(row.createdAt) }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </details>
                </div>
            }
        }
    } @else {
        @for (day of saveDays(); track day.key) {
            <div class="fw-semibold text-body-secondary mt-4 mb-2">{{ day.heading }}</div>

            @for (save of day.items; track save.correlationId) {
                <div class="callout callout-info my-2">
                    <div class="d-flex justify-content-between flex-wrap gap-2">
                        <span class="fw-semibold">
                            <i class="bi bi-save me-1"></i>
                            {{ save.events.length }} {{ save.events.length === 1 ? 'record' : 'records' }} changed
                            @if (save.customerName) {
                                <span class="fw-normal">
                                    <i class="bi bi-chevron-compact-right"></i>
                                    {{ save.customerName }}
                                </span>
                            }
                        </span>
                    </div>

                    <ul class="list-unstyled mt-1 mb-0">
                        @for (event of save.events; track eventKey(event)) {
                            <li>
                                <span class="badge me-1" [class]="kindBadgeClass(event.kind)">{{ event.kind.toUpperCase() }}</span>
                                <i class="bi me-1" [class]="tableDisplay(event.tableName).icon"></i>
                                {{ tableDisplay(event.tableName).label }}
                                <span class="font-monospace">{{ event.record }}</span>:
                                {{ event.summary }}
                            </li>
                        }
                    </ul>

                    <div class="small text-body-secondary d-flex flex-wrap column-gap-4 mt-1">
                        <span>{{ formatTime(save.occurredAt) }}</span>
                        <span>by {{ save.userName }}</span>
                    </div>
                </div>
            }
        }
    }

    @if (loadFailed()) {
        <div class="alert alert-danger mt-3" role="alert">
            <i class="bi bi-exclamation-triangle-fill me-2" aria-hidden="true"></i>The history could not be loaded.
            <button type="button" class="btn btn-sm btn-outline-danger ms-2" (click)="retry.emit()">Try again</button>
        </div>
    } @else if (!events().length) {
        @if (loading()) {
            <div class="text-center my-4" role="status">
                <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span> Loading history...
            </div>
        } @else if (showEmptyMessage()) {
            <div class="alert alert-info mt-3">
                <i class="bi bi-info-circle me-2" aria-hidden="true"></i>No logged changes.
            </div>
        }
    } @else if (hasMore() || loading()) {
        <div class="text-center my-3">
            <button type="button" class="btn btn-outline-primary" [disabled]="loading()" (click)="loadMore.emit()">
                @if (loading()) {
                    <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
                }
                Load more
            </button>
            <span class="visually-hidden" role="status">{{ loading() ? 'Loading more history' : '' }}</span>
        </div>
    }
</div>
